import format from 'date-fns/format';
import { call, delay } from 'redux-saga/effects';
import removeAcentoUtil from '@giro-pdv/utils/removeAcento.util';
import { PinpadError, requestAgent, REQUEST_TIMEOUT } from './pinpad.agent';

export type Connection = {
  sessionId: string;
  port: string;
  terminal: string;
  key: string;
  identifiedAt: number;
};
type Discovery = { sessionId: string; fiscal: Record<string, string> };
export type ConnectionContext = {
  connection: Connection | null;
  discovery: Discovery | null;
  transaction?: Discovery | null;
  recovery?: Discovery | null;
};

const dicoveryLen = (str) => String(String(str).length).padStart(2, '0');

const converToStr = (obj) => {
  const arr = Object.keys(obj).map((o) => [o, obj[o].join('')].join(''));
  return arr.join('');
};

const required = (detail: any, field: string) => {
  const value = detail?.[field];
  if (
    (typeof value !== 'string' && typeof value !== 'number') ||
    !String(value).trim()
  ) {
    throw new PinpadError(
      `Configuração do pinpad incompleta: ${field}.`,
      'configuration'
    );
  }
  return String(value).trim();
};

const configuredPort = (detail: any) =>
  (String(detail?.portaCom ?? '').trim() || 'AUTO_USB')
    .toUpperCase()
    .replace(/^COM(?=\d+$)/, '');

export const sessionConfig = (detail: any, terminal?: string) => {
  const client = required(detail, 'cdCliente');
  if (!/^\d{1,7}$/.test(client))
    throw new PinpadError(
      'Código do estabelecimento inválido para identificar o terminal.',
      'configuration'
    );
  const document = required(detail, 'nuCPFCNPJ');
  const storeId = required(detail, 'idLoja');
  const port = configuredPort(detail);
  if (!/^(AUTO_USB|\d+)$/.test(port)) {
    throw new PinpadError(
      'Porta do pinpad inválida. Informe AUTO_USB ou o número da porta COM.',
      'configuration'
    );
  }
  const descriptorMax22 = detail.descriptor.substring(0, 22);
  const razaoSocial = detail.nmRazaoSocial ?? '';
  const dataSubObj = {
    '00': [dicoveryLen(descriptorMax22), removeAcentoUtil(descriptorMax22)],
    '01': [dicoveryLen(detail.endereco), removeAcentoUtil(detail.endereco)],
    '02': [dicoveryLen(detail.cidade), removeAcentoUtil(detail.cidade)],
    '03': [dicoveryLen(detail.uf), detail.uf],
    '04': ['03', ' BR'],
    '05': [dicoveryLen(detail.cep), detail.cep],
    '06': [dicoveryLen(detail.mcc), detail.mcc.padStart(4, '0')],
    '07': [dicoveryLen(detail.nuCPFCNPJ), detail.nuCPFCNPJ],
    '08': ['11', detail.nuTelefone.padStart(11, '0')],
    '09': [dicoveryLen(detail.tid), detail.tid],
    '10': [dicoveryLen(detail.email), detail.email],
    '11': [dicoveryLen(razaoSocial), removeAcentoUtil(razaoSocial)],
    '12': ['01', detail.nuCPFCNPJ.length > 11 ? 'J' : 'F'],
    '13': ['15', detail.mid.padStart(15, '0')],
  };
  const sub = converToStr(dataSubObj);
  return {
    sitefIp: '127.0.0.1',
    storeId,
    terminalId: terminal || client.substring(0, 4) + document.substring(0, 4),
    sessionParameters: `[PortaPinPad=${port}];[ParmsClient=1=${document};2=23041219000134];[DadosSubAdquirencia=${sub}]`,
  };
};

export const fiscalNow = (invoice: string) => {
  const now = new Date();
  return {
    taxInvoiceNumber: invoice,
    taxInvoiceDate: format(now, 'yyyyMMdd'),
    taxInvoiceTime: format(now, 'HHmmss'),
  };
};

export function* agentState(): Generator<any, any, any> {
  // Only the read-only state request is retried, with a bounded delay.
  for (let attempt = 0; ; attempt++) {
    try {
      const state = yield call(requestAgent, '/state', 'GET');
      if (![0, 1, 2, 3, 4].includes(state.serviceState)) {
        throw new PinpadError('Estado inválido do agente do pinpad.', 'agent');
      }
      return state;
    } catch (error) {
      if (
        attempt === 1 ||
        !(error instanceof PinpadError) ||
        !['network', 'timeout'].includes(error.kind)
      )
        throw error;
      yield delay(500);
    }
  }
}

export const requireIdle = (state: any) => {
  if (state.serviceState !== 0 && state.serviceState !== 1) {
    throw new PinpadError(
      'O pinpad está ocupado com uma operação pendente. Conclua ou recupere essa operação antes de conectar novamente.',
      'busy'
    );
  }
};

function* createSession(
  config: ReturnType<typeof sessionConfig>
): Generator<any, string, any> {
  const result = yield call(requestAgent, '/session', 'POST', config, [0]);
  if (!result.sessionId)
    throw new PinpadError('O agente não retornou uma sessão válida.', 'agent');
  return result.sessionId;
}

function* deleteIdleSession(
  context: ConnectionContext,
  sessionId: string
): Generator<any, void, any> {
  let state = yield call(agentState);
  if (state.sessionId !== sessionId)
    throw new PinpadError(
      'A sessão do pinpad mudou. Tente conectar novamente.',
      'session'
    );
  state = yield call(recoverPending, context, state);
  requireIdle(state);
  yield call(requestAgent, '/session', 'DELETE', { sessionId }, [0]);
}

function* ensurePresent(
  context: ConnectionContext,
  session: Pick<Connection, 'sessionId' | 'port'>,
  detail: any,
  terminal: string
): Generator<any, Pick<Connection, 'sessionId' | 'port'>, any> {
  try {
    yield call(
      requestAgent,
      '/pinpad/isPresent',
      'POST',
      { sessionId: session.sessionId },
      [1]
    );
    return session;
  } catch (error) {
    // Retry only a device failure on port 99, never an ambiguous agent failure.
    if (
      session.port !== '99' ||
      !(error instanceof PinpadError) ||
      error.kind !== 'absent' ||
      ![0, -43].includes(error.details.clisitefStatus)
    )
      throw error;
  }
  yield call(deleteIdleSession, context, session.sessionId);
  const port = 'AUTO_USB';
  const sessionId = yield call(
    createSession,
    sessionConfig({ ...detail, portaCom: port }, terminal)
  );
  // A failed USB check propagates; there is only one fallback per connection.
  yield call(requestAgent, '/pinpad/isPresent', 'POST', { sessionId }, [1]);
  return { sessionId, port };
}

// The recovery owns one session and one fiscal tuple until the agent is idle.
function* recoverPending(
  context: ConnectionContext,
  state: any
): Generator<any, any, any> {
  if (state.serviceState === 0 || state.serviceState === 1) {
    context.discovery = null;
    context.recovery = null;
    return state;
  }
  const sessionId = state.sessionId;
  if (!sessionId)
    throw new PinpadError(
      'O agente ocupado não informou a sessão para recuperação.',
      'session'
    );
  context.connection = null;
  const original = [
    context.recovery,
    context.discovery,
    context.transaction,
  ].find((operation) => operation?.sessionId === sessionId);
  const placeholder = fiscalNow('999999');
  const originalDate = original?.fiscal.taxInvoiceDate || '';
  const originalTime = original?.fiscal.taxInvoiceTime || '';
  const fiscal = {
    taxInvoiceNumber: String(
      original?.fiscal.taxInvoiceNumber || placeholder.taxInvoiceNumber
    ),
    taxInvoiceDate: /^\d{8}$/.test(originalDate)
      ? originalDate
      : placeholder.taxInvoiceDate,
    taxInvoiceTime: /^\d{6}$/.test(originalTime)
      ? originalTime
      : placeholder.taxInvoiceTime,
  };
  context.recovery = { sessionId, fiscal };
  const deadline = Date.now() + 45000;
  const remaining = () => {
    const timeout = Math.min(deadline - Date.now(), REQUEST_TIMEOUT);
    if (timeout <= 0)
      throw new PinpadError(
        'Tempo limite para recuperar a operação pendente do pinpad.',
        'timeout'
      );
    return timeout;
  };
  const checkSession = (current: any) => {
    if (current.sessionId !== sessionId)
      throw new PinpadError(
        'A sessão do pinpad mudou durante a recuperação.',
        'session'
      );
  };
  for (
    let step = 0;
    state.serviceState === 2 || state.serviceState === 3;
    step++
  ) {
    if (step >= 80)
      throw new PinpadError(
        'Limite de tentativas para recuperar a operação pendente do pinpad.',
        'busy'
      );
    const result = yield call(
      requestAgent,
      '/continueTransaction',
      'POST',
      { sessionId, data: '', continue: 0 },
      'transaction',
      remaining()
    );
    state = yield call(
      requestAgent,
      '/state',
      'GET',
      undefined,
      undefined,
      remaining()
    );
    checkSession(state);
    if (result.clisitefStatus !== 10000) break;
    if (result.commandId === 23 && [2, 3].includes(state.serviceState))
      yield delay(Math.min(100, remaining()));
  }
  if (state.serviceState === 4) {
    yield call(
      requestAgent,
      '/finishTransaction',
      'POST',
      { sessionId, confirm: 0, ...fiscal },
      [0],
      remaining()
    );
    state = yield call(
      requestAgent,
      '/state',
      'GET',
      undefined,
      undefined,
      remaining()
    );
    checkSession(state);
  }
  requireIdle(state);
  context.discovery = null;
  context.recovery = null;
  if (context.transaction?.sessionId === sessionId) context.transaction = null;
  return state;
}

function* identify(
  context: ConnectionContext,
  sessionId: string,
  client: string
): Generator<any, string, any> {
  const fiscal = fiscalNow('999999');
  context.discovery = { sessionId, fiscal };
  const deadline = Date.now() + 45000;
  let result = yield call(
    requestAgent,
    '/startTransaction',
    'POST',
    {
      sessionId,
      functionId: 775,
      trnAmount: '0',
      ...fiscal,
      cashierOperator: 'PDV',
      trnAdditionalParameters: '',
    },
    [0, 10000]
  );
  let serial = '';
  for (let step = 0; result.clisitefStatus === 10000; step++) {
    const remaining = deadline - Date.now();
    if (step >= 80 || remaining <= 0)
      throw new PinpadError(
        'Tempo limite para identificar o pinpad.',
        'timeout'
      );
    result = yield call(
      requestAgent,
      '/continueTransaction',
      'POST',
      {
        sessionId,
        data: '',
        continue: 0,
      },
      [0, 10000],
      Math.min(remaining, REQUEST_TIMEOUT)
    );
    if (result.clisitefStatus === 10000) {
      if (result.commandId === 0 && result.fieldId === 2455)
        serial = String(result.data || '').replace(/\D/g, '');
      if (
        ![0, 1, 2, 3, 4, 11, 12, 13, 14, 15, 16, 23].includes(result.commandId)
      ) {
        throw new PinpadError(
          'O agente solicitou uma interação inesperada durante a identificação.',
          'agent'
        );
      }
      if (result.commandId === 23) yield delay(100);
    }
  }
  yield call(
    requestAgent,
    '/finishTransaction',
    'POST',
    { sessionId, confirm: 1, ...fiscal },
    [0]
  );
  context.discovery = null;
  if (!serial || !/^\d{1,7}$/.test(client))
    throw new PinpadError(
      'Identificação do pinpad incompleta.',
      'identification'
    );
  return (
    client + serial.padStart(8 - client.length, '0').slice(-(8 - client.length))
  );
}

export function* connectPinpad(
  context: ConnectionContext,
  detail: any,
  forceIdentify = true
): Generator<any, Connection, any> {
  const config = sessionConfig(detail);
  const key = JSON.stringify(config);
  let state = yield call(agentState);
  state = yield call(recoverPending, context, state);
  requireIdle(state);
  const previous = context.connection;
  const reusable =
    previous?.sessionId === state.sessionId && previous?.key === key;
  const configuredTerminal = reusable ? previous.terminal : config.terminalId;
  context.connection = null;
  if (!reusable) {
    if (state.sessionId)
      yield call(deleteIdleSession, context, state.sessionId);
  }
  let session = {
    sessionId: reusable ? state.sessionId : yield call(createSession, config),
    port: reusable ? previous.port : configuredPort(detail),
  };
  session = yield call(
    ensurePresent,
    context,
    session,
    detail,
    configuredTerminal
  );
  const reuseIdentification =
    reusable && session.sessionId === previous.sessionId;
  let terminal = configuredTerminal;
  let identifiedAt = reuseIdentification ? previous.identifiedAt : 0;
  if (
    !reuseIdentification ||
    forceIdentify ||
    Date.now() - identifiedAt >= 60000
  ) {
    terminal = yield call(
      identify,
      context,
      session.sessionId,
      required(detail, 'cdCliente')
    );
    identifiedAt = Date.now();
    if (terminal !== configuredTerminal) {
      yield call(deleteIdleSession, context, session.sessionId);
      session.sessionId = yield call(
        createSession,
        sessionConfig({ ...detail, portaCom: session.port }, terminal)
      );
      session = yield call(ensurePresent, context, session, detail, terminal);
    }
  }
  const connection = { ...session, terminal, key, identifiedAt };
  context.connection = connection;
  return connection;
}

export function* disconnectPinpad(
  context: ConnectionContext
): Generator<any, void, any> {
  let state = yield call(agentState);
  const sessionId = context.connection?.sessionId;
  state = yield call(recoverPending, context, state);
  requireIdle(state);
  if (sessionId === state.sessionId)
    yield call(deleteIdleSession, context, state.sessionId);
  context.connection = null;
}
